import { orderBy } from 'lodash-es';
import { useMemo } from 'react';

import { Typography } from '@speeki/global-ui-components';

import {
    DashboardStatisticsDocument,
    EsgStatisticsDocument,
} from '@graphql/generated/graphql';

import {
    type CategoryName,
    mapEsgCategoryToUiCategory,
} from '@utils/esgCategory';
import { useUserModules } from '@utils/hooks/useUserModules';

import { useQuery } from '@apollo/client';

import { StatisticWithLabel } from './Statistics.styled';

export type { CategoryName } from '@utils/esgCategory';

export interface StatisticsRiskArea {
    categoryName: CategoryName;
    firstQuestionUuid?: string;
    name: string;
    path: string;
    programmeId: string;
    statistics: {
        answeredQuestions: number;
        percent: number;
        totalQuestions: number;
    };
    uuid: string;
}

export const useStatistics = () => {
    const {
        hasAllAccess,
        hasDisclosureAccess,
        hasEsgAccess,
        hasIncidentsAccess,
        hasMetricsAccess,
        hasSurveyAccess,
        hasTasksManagerAccess,
    } = useUserModules();

    const { data: statisticsData, loading: statisticsDataLoading } = useQuery(
        DashboardStatisticsDocument,
        {
            context: {
                notifyOnError: true,
            },
            skip:
                !hasIncidentsAccess && !hasSurveyAccess && !hasDisclosureAccess,
            variables: {
                hasDisclosureAccess,
                hasIncidentsAccess,
                hasSurveyAccess,
            },
        },
    );

    const { data: esgStatisticsData, loading: esgStatisticsDataLoading } =
        useQuery(EsgStatisticsDocument, {
            skip: !hasEsgAccess,
            variables: {
                includeDisabledRiskAreas: false,
                input: {
                    categoryUuids: [],
                    riskAreaUuids: [],
                    textSearch: '',
                },
            },
        });

    const parsedRiskAreas: StatisticsRiskArea[] = useMemo(
        () =>
            esgStatisticsData?.programsList?.flatMap((program) => {
                const categoryName = mapEsgCategoryToUiCategory(
                    program.category.name,
                );

                if (!categoryName) {
                    return [];
                }

                const primaryRiskArea = program.riskAreas[0];

                if (!primaryRiskArea) {
                    return [];
                }

                const answeredQuestions = program.riskAreas.reduce(
                    (sum, riskArea) =>
                        sum + (riskArea.statistics?.answeredQuestions ?? 0),
                    0,
                );

                const totalQuestions = program.riskAreas.reduce(
                    (sum, riskArea) =>
                        sum + (riskArea.statistics?.totalQuestions ?? 0),
                    0,
                );

                const firstQuestionUuid = program.riskAreas
                    .flatMap((riskArea) => riskArea.steps)
                    .flatMap((step) => step.questions)
                    .find((question) => !!question.uuid)?.uuid;

                return [
                    {
                        uuid: primaryRiskArea.uuid,
                        categoryName,
                        firstQuestionUuid,
                        name: program.name,
                        path: primaryRiskArea.path,
                        programmeId: program.uuid,
                        statistics: {
                            answeredQuestions,
                            percent:
                                totalQuestions > 0
                                    ? Math.floor(
                                          (answeredQuestions / totalQuestions) *
                                              100,
                                      )
                                    : 0,
                            totalQuestions,
                        },
                    },
                ];
            }) ?? [],
        [esgStatisticsData],
    );

    //NOTE: we need 3 elements - one from each category with highest percent
    const mostAnsweredRiskAreas = useMemo(() => {
        const groupedByCategory = parsedRiskAreas.reduce(
            (acc, riskArea) => {
                const category = riskArea.categoryName;
                if (
                    !acc[category] ||
                    acc[category].statistics.percent <
                        riskArea.statistics.percent
                ) {
                    acc[category] = riskArea;
                }
                return acc;
            },
            {} as Record<CategoryName, StatisticsRiskArea>,
        );

        return orderBy(
            Object.values(groupedByCategory),
            ['statistics.percent'],
            ['desc'],
        );
    }, [parsedRiskAreas]);

    const incidentsStatisticsData =
        statisticsData?.incidentsStatistics?.statistics;
    const surveysStatisticsData = statisticsData?.surveyStatistics?.statistics;
    const collectStatisticsData = statisticsData?.collectStatistics?.statistics;

    const incidentsStatistics = [
        {
            label: 'total incidents',
            value: incidentsStatisticsData?.totalCount || 0,
        },
        {
            label: 'active incident(s)',
            value: incidentsStatisticsData?.openedCount || 0,
        },
        {
            label: 'total incidents closed',
            value: incidentsStatisticsData?.closedCount || 0,
        },
    ];

    const surveyStatistics = [
        {
            label: `completed pulses`,
            value: surveysStatisticsData?.completedCount ?? 0,
        },
        {
            label: `ongoing pulses`,
            value: surveysStatisticsData?.ongoingCount ?? 0,
        },
        {
            label: `scheduled pulses`,
            value: surveysStatisticsData?.scheduledCount ?? 0,
        },
    ];

    const collectStatistics = [
        {
            label: 'forms',
            value: collectStatisticsData?.disclosuresCount ?? 0,
        },
        {
            label: (
                <StatisticWithLabel
                    hasLabel={!!collectStatisticsData?.newAnswersCount}
                >
                    <Typography.TextBody_10_16>
                        filled out forms
                    </Typography.TextBody_10_16>
                    {!!collectStatisticsData?.newAnswersCount && (
                        <Typography.TextBody_10_16>
                            ({collectStatisticsData.newAnswersCount} new
                            submissions)
                        </Typography.TextBody_10_16>
                    )}
                </StatisticWithLabel>
            ),
            value: collectStatisticsData?.responsesCount ?? 0,
        },
        {
            label: 'approved requests',
            value: collectStatisticsData?.approvedCount ?? 0,
        },
    ];

    return {
        access: {
            hasAllAccess,
            hasDisclosureAccess,
            hasEsgAccess,
            hasIncidentsAccess,
            hasMetricsAccess,
            hasSurveyAccess,
            hasTasksManagerAccess,
        },
        loading: {
            esgStatisticsDataLoading,
            statisticsDataLoading,
        },
        statistics: {
            collectStatistics,
            incidentsStatistics,
            mostAnsweredRiskAreas,
            surveyStatistics,
        },
    };
};
